import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeature, useGrowthBook } from '@growthbook/growthbook-react';
import DashboardLayout from '../../layouts/DashoardLayout';
import { Box, CircularProgress, Paper, Stack, Typography } from '../../ui/core';
import { surfaceCardSx as cardSx, cardHeaderSx, cardBodySx } from '../../ui/styles';
import api from '../../lib/api';
import CreateExclusionPanel from './CreateExclusionPanel';
import ActiveExclusionsTable from './ActiveExclusionsTable';
import HistoryExclusionsTable from './HistoryExclusionsTable';

// Single-column layout: a plain page header, then three stacked cards
// (create / active / history), matching the trip-throttling page's scaffold.
const pageSx = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  p: '12px',
  minHeight: '100vh',
  overflowY: 'auto',
};

const HISTORY_PAGE_LIMIT = 50;
const ACTIVE_PAGE_LIMIT = 50;

const RiderDriverExclusionsShell = () => {
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingMoreActive, setLoadingMoreActive] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  // Active is cursor-paginated like history — never capped at a single page, so
  // no active exclusion is ever hidden (and thus un-removable) past the first N.
  const [activeCursor, setActiveCursor] = useState(undefined);
  const [activeExhausted, setActiveExhausted] = useState(false);
  // Simpler than trip-throttling's two-stream history (which merges CANCELLED +
  // EXPIRED): removed exclusions are one stream, so a single cursor plus a
  // single "exhausted" flag is enough. `cursor: null/undefined` means "first
  // page" to the API (getExclusions omits the param when falsy) — the same
  // value `nextCursor` takes on once the stream is exhausted — so exhaustion is
  // tracked in its own flag rather than inferred from the cursor.
  const [historyCursor, setHistoryCursor] = useState(undefined);
  const [historyExhausted, setHistoryExhausted] = useState(false);
  const [activeError, setActiveError] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  // Bumped by every refetch (full reset). A `loadMore*` in flight when a
  // create/remove-triggered refetch lands captures the generation it started
  // under; if that generation is stale by the time the page-2 response
  // arrives, its result is discarded instead of being appended onto (and its
  // cursor overwriting) the newer, unrelated snapshot the refetch just set.
  const activeGenerationRef = useRef(0);
  const historyGenerationRef = useRef(0);
  // Synchronous in-flight guards for the two "Load more" actions: the
  // `disabled={loadingMore}` prop only takes effect after React re-renders,
  // so two rapid clicks in the same tick would otherwise both capture the
  // same cursor/generation, both append their (identical) page, and produce
  // duplicate rows. Set/cleared outside of React state so a second call this
  // tick can see it immediately, before any render happens.
  const activeLoadMoreInFlightRef = useRef(false);
  const historyLoadMoreInFlightRef = useRef(false);

  const refetchActive = useCallback(async () => {
    const generation = ++activeGenerationRef.current;
    setLoadingActive(true);
    try {
      const result = await api.riderDriverExclusions.getExclusions({ status: 'ACTIVE', limit: ACTIVE_PAGE_LIMIT });
      if (activeGenerationRef.current !== generation) return;
      setActive((result && result.entries) || []);
      setActiveCursor(result ? result.nextCursor || null : null);
      setActiveExhausted(!(result && result.nextCursor));
      // A failed load must not masquerade as an empty list — clear the error
      // only on success, and set it in the catch so the card shows "couldn't
      // load" instead of the (misleading) empty state.
      setActiveError(false);
    } catch (err) {
      if (activeGenerationRef.current !== generation) return;
      setActiveError(true);
    } finally {
      // Unconditional: this fetch is done regardless of whether a newer
      // refetch superseded it. Gating this on the generation left a
      // superseded load-more's "Loading…" button stuck on forever, since
      // nothing else ever clears it.
      setLoadingActive(false);
    }
  }, []);

  const loadMoreActive = useCallback(async () => {
    if (activeLoadMoreInFlightRef.current) return;
    activeLoadMoreInFlightRef.current = true;
    const generation = activeGenerationRef.current;
    setLoadingMoreActive(true);
    try {
      const result = await api.riderDriverExclusions.getExclusions({
        status: 'ACTIVE',
        cursor: activeCursor,
        limit: ACTIVE_PAGE_LIMIT,
      });
      if (activeGenerationRef.current !== generation) return;
      setActive((prev) => [...prev, ...((result && result.entries) || [])]);
      setActiveCursor(result ? result.nextCursor || null : activeCursor);
      setActiveExhausted(!(result && result.nextCursor));
      setActiveError(false);
    } catch (err) {
      if (activeGenerationRef.current !== generation) return;
      // Keep the loaded rows + cursor so "Load more" stays available to retry.
      setActiveError(true);
    } finally {
      activeLoadMoreInFlightRef.current = false;
      setLoadingMoreActive(false);
    }
  }, [activeCursor]);

  const refetchHistory = useCallback(async () => {
    const generation = ++historyGenerationRef.current;
    setLoadingHistory(true);
    try {
      const result = await api.riderDriverExclusions.getExclusions({ status: 'REMOVED', limit: HISTORY_PAGE_LIMIT });
      if (historyGenerationRef.current !== generation) return;
      setHistory((result && result.entries) || []);
      setHistoryCursor(result ? result.nextCursor || null : null);
      setHistoryExhausted(!(result && result.nextCursor));
      setHistoryError(false);
    } catch (err) {
      if (historyGenerationRef.current !== generation) return;
      setHistoryError(true);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadMoreHistory = useCallback(async () => {
    if (historyLoadMoreInFlightRef.current) return;
    historyLoadMoreInFlightRef.current = true;
    const generation = historyGenerationRef.current;
    setLoadingMoreHistory(true);
    try {
      const result = await api.riderDriverExclusions.getExclusions({
        status: 'REMOVED',
        cursor: historyCursor,
        limit: HISTORY_PAGE_LIMIT,
      });
      if (historyGenerationRef.current !== generation) return;
      setHistory((prev) => [...prev, ...((result && result.entries) || [])]);
      setHistoryCursor(result ? result.nextCursor || null : historyCursor);
      setHistoryExhausted(!(result && result.nextCursor));
      setHistoryError(false);
    } catch (err) {
      if (historyGenerationRef.current !== generation) return;
      // Leave the already-loaded rows and the cursor as-is so "Load more"
      // stays available for a retry rather than swallowing the failure.
      setHistoryError(true);
    } finally {
      historyLoadMoreInFlightRef.current = false;
      setLoadingMoreHistory(false);
    }
  }, [historyCursor]);

  const refetchBoth = useCallback(() => {
    refetchActive();
    refetchHistory();
  }, [refetchActive, refetchHistory]);

  useEffect(() => {
    refetchActive();
    refetchHistory();
  }, [refetchActive, refetchHistory]);

  return (
    <Box sx={pageSx}>
      {/* Wider than the throttling page's 960: the exclusion tables carry two
          full 24-char Mongo ids plus provenance + a Remove action, which need
          the room to lay out without the last column overflowing. */}
      <Stack spacing="16px" sx={{ maxWidth: 1120, width: '100%' }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.01em' }}>
            Rider-Driver Exclusions
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '13px', mt: '4px' }}>
            Rider-driver pairs ops has flagged not to ride together. Manual entries only.
          </Typography>
        </Box>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Add an exclusion</Typography>
          </Box>
          <Box sx={cardBodySx}>
            <CreateExclusionPanel onCreated={refetchActive} />
          </Box>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Active exclusions</Typography>
          </Box>
          <Box sx={cardBodySx}>
            {activeError && active.length === 0 ? (
              <Typography color="error" sx={{ fontSize: '13px' }}>
                Couldn't load active exclusions. Refresh to try again.
              </Typography>
            ) : (
              <>
                {activeError && (
                  <Typography color="error" sx={{ fontSize: '13px', mb: '8px' }}>
                    Couldn't refresh active exclusions — showing the last loaded results.
                  </Typography>
                )}
                <ActiveExclusionsTable
                  rows={active}
                  loading={loadingActive}
                  hasMore={!activeExhausted}
                  loadingMore={loadingMoreActive}
                  onLoadMore={loadMoreActive}
                  onRemoved={refetchBoth}
                />
              </>
            )}
          </Box>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>History (removed)</Typography>
          </Box>
          <Box sx={cardBodySx}>
            {historyError && history.length === 0 ? (
              <Typography color="error" sx={{ fontSize: '13px' }}>
                Couldn't load removed exclusions. Refresh to try again.
              </Typography>
            ) : (
              <>
                {historyError && (
                  <Typography color="error" sx={{ fontSize: '13px', mb: '8px' }}>
                    Couldn't refresh removed exclusions — showing the last loaded results.
                  </Typography>
                )}
                <HistoryExclusionsTable
                  rows={history}
                  loading={loadingHistory}
                  hasMore={!historyExhausted}
                  loadingMore={loadingMoreHistory}
                  onLoadMore={loadMoreHistory}
                />
              </>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export { RiderDriverExclusionsShell };

// The route is registered unconditionally (see App.js) so a flagged-off
// deep-link 404s here instead of the Switch's trailing catch-all silently
// redirecting to /home. `useFeature` reads the same GrowthBook context
// DashoardLayout's nav item does, so the two stay consistent.
//
// App.js kicks off `growthbook.loadFeatures()` app-wide but doesn't block
// rendering on it (a flag-service hiccup shouldn't be able to stall login).
// That means a direct deep-link here can render before this flag's real value
// has arrived, which would 404-flash a page that's actually on. So this route
// alone awaits its own readiness — everywhere else in the app stays unblocked.
//
// Unlike /trip-throttling, there is NO extra permission gate here: any admin
// may manage exclusions for v1. The page gates on flagReady + flagOn only.
const RiderDriverExclusions = () => {
  const growthbook = useGrowthBook();
  const flagOn = useFeature('admin-rider-driver-exclusions').on;
  const [flagReady, setFlagReady] = useState(!!growthbook?.ready);

  useEffect(() => {
    if (!growthbook || flagReady) return undefined;
    let cancelled = false;
    growthbook.loadFeatures({ timeout: 2000 }).finally(() => {
      if (!cancelled) setFlagReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [growthbook, flagReady]);

  if (!flagReady) {
    return (
      <DashboardLayout title="Rider-Driver Exclusions">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: '48px' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (!flagOn) {
    return (
      <DashboardLayout title="Not found">
        <Typography>Page not found.</Typography>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout flush title="Rider-Driver Exclusions">
      <RiderDriverExclusionsShell />
    </DashboardLayout>
  );
};

export default RiderDriverExclusions;
